import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { PlusCircle, HelpCircle, Archive, Edit2 } from 'lucide-react';
import Swal from 'sweetalert2';

const AlmacenInsumos = ({ isATC, isAdminOrGerente }) => {
    const [insumos, setInsumos] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [cuentas, setCuentas] = useState([]);

    // Form states
    const [tipo, setTipo] = useState('Correo');
    const [dato, setDato] = useState('');
    const [proveedor, setProveedor] = useState('');
    
    // Modal state for linking
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [currentInsumo, setCurrentInsumo] = useState(null);
    const [linkedCuenta, setLinkedCuenta] = useState('');
    const [editingInsumo, setEditingInsumo] = useState(null);
    // Filters
    const [filtroTipo, setFiltroTipo] = useState('Todos');
    const [filtroEstado, setFiltroEstado] = useState('Todos');

    useEffect(() => {
        const qInsumos = query(collection(db, 'almacen_insumos'), orderBy('created_at', 'desc'));
        const unsubInsumos = onSnapshot(qInsumos, (snap) => {
            setInsumos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const qProv = query(collection(db, 'proveedores'), orderBy('nombre', 'asc'));
        const unsubProv = onSnapshot(qProv, (snap) => {
            setProveedores(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        const qCuentas = query(collection(db, 'inventario_cuentas'), orderBy('correo_cuenta', 'asc'));
        const unsubCuentas = onSnapshot(qCuentas, (snap) => {
            setCuentas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => { unsubInsumos(); unsubProv(); unsubCuentas(); };
    }, []);

    const handleSaveInsumo = async (e) => {
        e.preventDefault();
        if (!dato.trim()) return Swal.fire('Error', 'El dato (correo/código) es requerido', 'error');
        
        try {
            if (editingInsumo) {
                await updateDoc(doc(db, 'almacen_insumos', editingInsumo.id), {
                    tipo,
                    dato: dato.trim(),
                    proveedor: proveedor || 'Sin Especificar'
                });
                setEditingInsumo(null);
                setDato('');
                Swal.fire({ title: '¡Insumo Actualizado!', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } else {
                await addDoc(collection(db, 'almacen_insumos'), {
                    tipo,
                    dato: dato.trim(),
                    proveedor: proveedor || 'Sin Especificar',
                    estado: 'Disponible',
                    fecha_ingreso: new Date().toISOString().slice(0, 10),
                    created_at: serverTimestamp()
                });
                setDato('');
                Swal.fire({ title: '¡Insumo Guardado!', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            }
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'No se pudo registrar el insumo', 'error');
        }
    };

    const handleOpenLinkModal = (insumo) => {
        setCurrentInsumo(insumo);
        setShowLinkModal(true);
    };

    const handleMarkAsUsed = async () => {
        if (!linkedCuenta) return Swal.fire('Error', 'Selecciona una cuenta vinculada', 'error');
        try {
            await updateDoc(doc(db, 'almacen_insumos', currentInsumo.id), {
                estado: 'Usado',
                cuenta_vinculada: linkedCuenta,
                fecha_uso: new Date().toISOString().slice(0, 10)
            });
            setShowLinkModal(false);
            setLinkedCuenta('');
            setCurrentInsumo(null);
            Swal.fire({ title: '¡Insumo Vinculado!', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'No se pudo actualizar el insumo', 'error');
        }
    };

    const filteredInsumos = insumos.filter(ins => {
        const matchTipo = filtroTipo === 'Todos' || ins.tipo === filtroTipo;
        const matchEstado = filtroEstado === 'Todos' || ins.estado === filtroEstado;
        return matchTipo && matchEstado;
    });

    return (
        <div className="space-y-6">
            {/* Formulario de Registro */}
            {!isATC && (
                <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                        <PlusCircle size={20} className="text-cyan-400" /> {editingInsumo ? 'Actualizar Insumo' : 'Registrar Nuevo Insumo'}
                    </h3>
                    <form onSubmit={handleSaveInsumo} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-slate-400 font-bold uppercase">Tipo de Insumo</label>
                            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 text-sm">
                                <option value="Correo">Correo Base</option>
                                <option value="GC">Gift Card</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1 md:col-span-2">
                            <label className="text-xs text-slate-400 font-bold uppercase">Dato (Correo / Código)</label>
                            <input 
                                type="text" 
                                value={dato} 
                                onChange={(e) => setDato(e.target.value)} 
                                placeholder={tipo === 'Correo' ? 'ejemplo@correo.com' : 'XXXX-XXXX-XXXX'}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 text-sm"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-xs text-slate-400 font-bold uppercase">Proveedor</label>
                            <select value={proveedor} onChange={(e) => setProveedor(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-cyan-500 text-sm">
                                <option value="">Seleccionar Proveedor</option>
                                {proveedores.map(p => (
                                    <option key={p.id} value={p.nombre}>{p.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-4 flex justify-end mt-2 gap-2">
                            {editingInsumo && (
                                <button type="button" onClick={() => { setEditingInsumo(null); setDato(''); }} className="bg-slate-700 hover:bg-slate-600 text-slate-200 font-bold px-4 py-2 rounded-lg transition-colors text-sm">
                                    Cancelar Edición
                                </button>
                            )}
                            <button type="submit" className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-6 py-2 rounded-lg transition-colors text-sm flex items-center gap-2">
                                <PlusCircle size={16} /> {editingInsumo ? 'Guardar Cambios' : 'Guardar Insumo'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Listado de Insumos */}
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 shadow-xl">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Archive size={20} className="text-cyan-400" /> Inventario de Insumos
                    </h3>
                    <div className="flex flex-wrap gap-2 w-full md:w-auto">
                        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none">
                            <option value="Todos">Todos los Tipos</option>
                            <option value="Correo">Correos</option>
                            <option value="GC">Gift Cards</option>
                        </select>
                        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none">
                            <option value="Todos">Todos los Estados</option>
                            <option value="Disponible">Disponibles</option>
                            <option value="Usado">Usados</option>
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-300">
                        <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                            <tr>
                                <th className="px-6 py-4">Tipo</th>
                                <th className="px-6 py-4">Dato</th>
                                <th className="px-6 py-4">Proveedor</th>
                                <th className="px-6 py-4">Fecha Ingreso</th>
                                <th className="px-6 py-4">Estado</th>
                                {!isATC && <th className="px-6 py-4 text-center">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {filteredInsumos.length === 0 ? (
                                <tr><td colSpan="6" className="text-center py-8 text-slate-500">Sin insumos registrados</td></tr>
                            ) : filteredInsumos.map((ins) => (
                                <tr key={ins.id} className="hover:bg-slate-700/20">
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${ins.tipo === 'Correo' ? 'bg-indigo-500/10 text-indigo-400' : 'bg-pink-500/10 text-pink-400'}`}>
                                            {ins.tipo === 'Correo' ? '📧 Correo' : '🎫 Gift Card'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-white text-xs">{ins.dato}</td>
                                    <td className="px-6 py-4">{ins.proveedor}</td>
                                    <td className="px-6 py-4 text-slate-400">{ins.fecha_ingreso}</td>
                                    <td className="px-6 py-4">
                                        {ins.estado === 'Disponible' ? (
                                            <span className="text-emerald-400 font-bold text-xs bg-emerald-500/10 px-2 py-1 rounded-full">Disponible</span>
                                        ) : (
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-slate-400 font-bold text-xs bg-slate-700 px-2 py-1 rounded-full w-fit">Usado</span>
                                                <span className="text-[10px] text-slate-500 italic">En: {ins.cuenta_vinculada}</span>
                                            </div>
                                        )}
                                    </td>
                                    {!isATC && (
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center gap-2">
                                                <button onClick={() => {
                                                    setEditingInsumo(ins);
                                                    setTipo(ins.tipo || 'Correo');
                                                    setDato(ins.dato || '');
                                                    setProveedor(ins.proveedor || '');
                                                }} title="Editar" className="text-slate-400 hover:text-cyan-400 p-1 flex items-center"><Edit2 size={16} /></button>
                                                {ins.estado === 'Disponible' && (
                                                    <button onClick={() => handleOpenLinkModal(ins)} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs px-2 py-1 rounded font-bold transition-colors">
                                                        Usar
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal para Vincular Cuenta */}
            {showLinkModal && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700 shadow-2xl p-6">
                        <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                            <HelpCircle size={20} className="text-emerald-400" /> ¿En qué cuenta se utilizó?
                        </h4>
                        <p className="text-slate-400 text-xs mb-4">
                            Vincula el insumo a un correo de inventario activo para mantener la trazabilidad.
                        </p>
                        <div className="flex flex-col gap-1 mb-6">
                            <label className="text-xs text-slate-400 font-bold uppercase">Cuenta de Destino</label>
                            <select value={linkedCuenta} onChange={(e) => setLinkedCuenta(e.target.value)} className="bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 text-sm">
                                <option value="">Seleccionar Cuenta</option>
                                {cuentas.map(c => (
                                    <option key={c.id} value={c.correo_cuenta}>{c.servicio} - {c.correo_cuenta}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => setShowLinkModal(false)} className="bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                                Cancelar
                            </button>
                            <button onClick={handleMarkAsUsed} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                                Confirmar Uso
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AlmacenInsumos;
